import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CharacterEditor from "../components/CharacterEditor.js";
import CharacterSheet from "../components/CharacterSheet.js";
import { useRtdbValue } from "../firebase/listeners.js";
import type { EditableCharacter } from "../lib/character-edits.js";
import { useAuthStore } from "../stores/auth.js";
import { useTableStore } from "../stores/table.js";

interface Member {
  role: "gm" | "gm_assistant" | "pc" | "audience";
}

export default function Sheet(): ReactElement {
  const { t } = useTranslation();
  const { charId } = useParams<{ charId: string }>();
  const tableId = useTableStore((s) => s.tableId);
  const uid = useAuthStore((s) => s.uid);
  const path = tableId && charId ? `tables/${tableId}/characters/${charId}` : null;
  const { data: character } = useRtdbValue<EditableCharacter>(path);
  const { data: member } = useRtdbValue<Member>(
    tableId && uid ? `tables/${tableId}/meta/members/${uid}` : null,
  );
  const [editing, setEditing] = useState(false);

  if (!tableId || !charId) return <p className="p-4">missing context</p>;
  if (!character) return <p className="p-4">{t("app.loading")}</p>;

  const canEdit =
    member?.role === "gm" ||
    member?.role === "gm_assistant" ||
    (character.uid === uid && member?.role !== "audience");

  return (
    <div>
      {canEdit && (
        <div className="flex justify-end p-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-xs"
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? t("edit.exit") : t("edit.enter")}
          </button>
        </div>
      )}
      {editing ? (
        <CharacterEditor
          characterPath={`/tables/${tableId}/characters/${charId}`}
          character={character}
        />
      ) : (
        <CharacterSheet tableId={tableId} charId={charId} isGM={member?.role === "gm"} />
      )}
    </div>
  );
}
